/******************************************************************************
 *
 * Copyright (c) 2019-2020 ServiceActivator Inc. All Rights Reserved.
 *
 * Unauthorized copying of the code in this file, completely or partially,
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 *
 * Written by 'Vishal Goel’ <vishal@wakencode.com>, February 2020
 *
 *****************************************************************************/

import indigoBus from "../assets/busImages/busmarker_indigo.png";
import greyBus from "../assets/busImages/busmarker_grey.png";
import pinkBus from "../assets/busImages/busmarker_pink.png";
import moment from "moment";
import uuid from "uniqid";
import { constant } from "./constants/constants";

// return bus color but vehicle status
export const busMarker = (selected, status) => {
  if (selected) return pinkBus;
  else if (status === constant.ONLINE) return indigoBus;
  else if (status === constant.OFFLINE) return greyBus;
  else return indigoBus;
};

// make coordinates compatible if they are coming in as a string
export const makeCoordinatesCompatible = (coordinates) => {
  if (!coordinates) return [];
  return coordinates.map((m) => {
    return {
      lat: parseFloat(m.latitude),
      lng: parseFloat(m.longitude),
    };
  });
};

// make coordinates compatible for post data
export const makeCoordinatesPostDataCompatible = (coordinates) => {
  return coordinates.map((m) => {
    return {
      latitude: m.lat,
      longitude: m.lng,
    };
  });
};

// process geometry data
export const processGeometryData = (allGeometry) => {
  let processedData = [];
  for (let geometry of allGeometry) {
    for (let g of geometry.geometries) {
      let compatibleCoordinates = makeCoordinatesCompatible(
        g.listOfCoordinates
      );
      processedData.push({
        id: uuid(),
        propertyName: geometry.propertyName,
        propertyId: geometry.propertyId,
        center: getCenter(compatibleCoordinates),
        paths: compatibleCoordinates,
      });
    }
  }
  return processedData;
};

// return vehicle last updated status
export const getUpdatedTimeStatus = (lastUpdatedTime) => {
  if (!lastUpdatedTime) return constant.UNKNOWN;
  let updatedStatusValue = "";

  // vehicle last update time status in ms
  let lastUpdatedTimeInMs = moment(lastUpdatedTime).utc().valueOf();

  // current time in ms
  let currentTimeInMs = moment().utc().valueOf();
  // difference between  current time and vehicle last update time
  let timeDiff = moment.duration(currentTimeInMs - lastUpdatedTimeInMs);

  // difference in seconds
  let seconds = timeDiff.seconds();

  // difference in hours
  let hours = timeDiff.hours();

  // difference in minutes
  let minutes = timeDiff.minutes();

  // difference in days
  let days = timeDiff.days();

  // difference in months
  let months = timeDiff.months();

  // if last updated in seconds
  if (!hours && !days && !minutes && !months && seconds) {
    updatedStatusValue = `${seconds}${" " + constant.SECS_AGO}`;
    // if last updated in minutes
  } else if (!hours && !days && !months && minutes) {
    updatedStatusValue = `${minutes}${" " + constant.MINS_AGO}`;
    // if last updated in hours
  } else if (!days && !months && hours) {
    updatedStatusValue = `${hours}${" " + constant.HRS_AGO}`;
    // if last updated in days
  } else if (!months && days) {
    updatedStatusValue = `${days}${" " + constant.DAYS_AGO}`;
    // if last updated in months
  } else {
    updatedStatusValue = `${months}${" " + constant.MONTHS_AGO}`;
  }
  return updatedStatusValue;
};

// calculate center of polygon
export const getCenter = (points) => {
  let lat = 0;
  let lng = 0;
  let totalPoints = points.length;

  for (let point of points) {
    lat = lat + point.lat;
    lng = lng + point.lng;
  }

  lat = lat / totalPoints;
  lng = lng / totalPoints;

  return { lat, lng };
};

// return true if given timestamp is smaller than than last 10 minutes else false
export const isBeforeTenMinutes = (timestamp) => {
  return moment(timestamp).isBefore(moment().subtract(10, "minutes"));
};

/**
 * return for which dates geometries are need to fetch
 * geometries need to fetch for startDate - 1, startDate, endDate, endDate + 1
 */
export const getTimestamps = (startDateTime, endDateTime) => {
  let dates = [];
  let dateFormat = "YYYYMMDD";

  let startDate = "";
  if (moment(startDateTime).hour() >= 18) {
    startDate = moment(startDateTime).add(1, "days").format(dateFormat, "L");
  } else {
    startDate = moment(startDateTime).format(dateFormat, "L");
  }
  dates.push(startDate);
  dates.push(
    moment(startDate, dateFormat).subtract(1, "days").format(dateFormat, "L")
  );

  let endDate = "";
  if (moment(endDateTime).hour() >= 18) {
    endDate = moment(endDateTime).add(1, "days").format(dateFormat, "L");
  } else {
    endDate = moment(endDateTime).format(dateFormat, "L");
  }
  dates.push(endDate);
  dates.push(
    moment(endDate, dateFormat).add(1, "days").format(dateFormat, "L")
  );

  let uniqueDates = [...new Set(dates)];

  return uniqueDates;
};
